import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../atoms/Button';
import Icon from '../atoms/Icon';
import SessionManager from '../../utils/sessionManager';

const LandingHeader = () => {
  const navigate = useNavigate();
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false);
  
  const currentSession = SessionManager.getCurrentSession();
  const isCandidate = currentSession?.role === 'candidate';

  const handleLogout = () => {
    SessionManager.clearAllSessions();
    navigate('/');
    window.location.reload();
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 relative">
          {/* Logo */}
          <div className="flex items-center">
            <span className="text-2xl font-bold text-gray-900 cursor-pointer" onClick={() => navigate('/')}>Aptirix</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {!currentSession && (
              <>
                <button onClick={() => navigate('/recruiter')} className="text-gray-900 hover:text-gray-700 font-medium">Recruitment</button>
                <button onClick={() => navigate('/placement-training')} className="text-gray-900 hover:text-gray-700 font-medium">Placement Training</button>
              </>
            )}
            {currentSession && (
              <>
                {!isCandidate && (
                  <button onClick={() => navigate('/recruiter')} className="text-gray-900 hover:text-gray-700 font-medium">Recruitment</button>
                )}
                <button onClick={() => navigate('/placement-training')} className="text-gray-900 hover:text-gray-700 font-medium">Placement Training</button>
              </>
            )}
            
            {/* Products Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setShowProductsDropdown(true)}
              onMouseLeave={() => setShowProductsDropdown(false)}
            >
              <button className="text-gray-900 hover:text-gray-700 flex items-center py-5 font-medium">
                Products
                <Icon name="expand_more" size={20} className="ml-1" />
              </button>
              
              {/* Dropdown Menu */}
              {showProductsDropdown && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-0 w-[800px] max-w-[95vw] bg-white rounded-2xl shadow-2xl border border-gray-200 p-8 z-[9999]">
                  <div className="grid grid-cols-2 gap-12">
                    {/* Left Column - Platform/Products */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-6">PLATFORM</h3>
                      <div className="space-y-0">
                        <button 
                          onClick={() => navigate('/ai-trainer')}
                          className="w-full flex items-start space-x-4 py-4 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-0"
                        >
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900 mb-1">AI Interview Trainer</div>
                            <div className="text-sm text-gray-900">Practice with AI feedback</div>
                          </div>
                        </button>
                        
                        <button 
                          onClick={() => navigate('/enhance-resume')}
                          className="w-full flex items-start space-x-4 py-4 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-0"
                        >
                          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900 mb-1">Enhance CV</div>
                            <div className="text-sm text-gray-900">AI-powered resume optimization</div>
                          </div>
                        </button>
                        
                        <button 
                          onClick={() => navigate('/jobs')}
                          className="w-full flex items-start space-x-4 py-4 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-0"
                        >
                          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900 mb-1">Mock Interviews</div>
                            <div className="text-sm text-gray-900">Real-time interview practice</div>
                          </div>
                        </button>
                      </div>
                    </div>
                    
                    {/* Right Column - Capabilities */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-6">CAPABILITIES</h3>
                      <div className="space-y-3">
                        <div className="text-sm text-gray-900 hover:text-gray-700 cursor-pointer py-2">AI-Powered Assessments</div>
                        <div className="text-sm text-gray-900 hover:text-gray-700 cursor-pointer py-2">Automated Screening</div>
                        <div className="text-sm text-gray-900 hover:text-gray-700 cursor-pointer py-2">Real-time Feedback</div>
                        <div className="text-sm text-gray-900 hover:text-gray-700 cursor-pointer py-2">Performance Analytics</div>
                        <div className="text-sm text-gray-900 hover:text-gray-700 cursor-pointer py-2">Career Guidance</div>
                        <div className="text-sm text-gray-900 hover:text-gray-700 cursor-pointer py-2">Skill Verification</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <a href="/jobs" className="text-gray-900 hover:text-gray-700 font-medium">Jobs</a>
            <a href="/courses" className="text-gray-900 hover:text-gray-700 font-medium">Courses</a>
            {/* Packages hidden for now
            {currentSession && isCandidate && (
              <a href="/candidate/packages" className="text-gray-900 hover:text-gray-700 font-medium">Packages</a>
            )}
            */}
            
            {/* Company Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setShowCompanyDropdown(true)}
              onMouseLeave={() => setShowCompanyDropdown(false)}
            >
              <button className="text-gray-900 hover:text-gray-700 flex items-center py-5 font-medium">
                Company
                <Icon name="expand_more" size={20} className="ml-1" />
              </button>
              
              {/* Company Dropdown Menu */}
              {showCompanyDropdown && (
                <div className="absolute top-full right-0 mt-0 w-[280px] bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 z-[9999]">
                  <div className="space-y-0">
                    <button 
                      onClick={() => {
                        setShowCompanyDropdown(false);
                        navigate('/about');
                      }}
                      className="w-full flex items-center space-x-3 py-3 hover:bg-gray-50 transition-colors text-left rounded-lg border-b border-gray-100 last:border-0"
                    >
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="font-medium text-gray-900">About Us</span>
                    </button>
                    
                    <button 
                      onClick={() => {
                        setShowCompanyDropdown(false);
                        navigate('/support');
                      }}
                      className="w-full flex items-center space-x-3 py-3 hover:bg-gray-50 transition-colors text-left rounded-lg border-b border-gray-100 last:border-0"
                    >
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                      <span className="font-medium text-gray-900">Help & Support</span>
                    </button>
                    
                    <button 
                      onClick={() => {
                        setShowCompanyDropdown(false);
                        navigate('/collaboration');
                      }}
                      className="w-full flex items-center space-x-3 py-3 hover:bg-gray-50 transition-colors text-left rounded-lg border-b border-gray-100 last:border-0"
                    >
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <span className="font-medium text-gray-900">Collaboration</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {!currentSession ? (
              <>
                <Button variant="outline" onClick={() => navigate('/login')}>
                  Sign In
                </Button>
                <Button onClick={() => navigate('/signup')} className="bg-black text-white hover:bg-gray-800">
                  Explore
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => navigate('/candidate/dashboard')}>
                  Dashboard
                </Button>
                <div 
                  className="relative"
                  onMouseEnter={() => setShowUserDropdown(true)}
                  onMouseLeave={() => setShowUserDropdown(false)}
                >
                  <div 
                    className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {currentSession.profile?.first_name?.charAt(0) || currentSession.email?.charAt(0) || 'U'}
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {currentSession.profile?.first_name || currentSession.email?.split('@')[0] || 'User'}
                    </span>
                    <Icon name="expand_more" size={16} className="text-gray-600" />
                  </div>
                  
                  {/* User Dropdown Menu */}
                  {showUserDropdown && (
                    <div className="absolute top-full right-0 mt-0 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-[9999]">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">
                          {currentSession.profile?.first_name || 'User'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {currentSession.email}
                        </p>
                      </div>
                      
                      <button
                        onClick={() => {
                          setShowUserDropdown(false);
                          navigate('/candidate/profile');
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <Icon name="person" size={18} className="text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">My Profile</span>
                      </button>
                      
                      {/* Packages hidden for now
                      <button
                        onClick={() => {
                          setShowUserDropdown(false);
                          navigate('/candidate/packages');
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <Icon name="card_giftcard" size={18} className="text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Packages</span>
                      </button>
                      */}
                      
                      <button
                        onClick={() => {
                          setShowUserDropdown(false);
                          navigate('/candidate/settings');
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <Icon name="settings" size={18} className="text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Settings</span>
                      </button>
                      
                      <div className="border-t border-gray-100 mt-2 pt-2">
                        <button
                          onClick={() => {
                            setShowUserDropdown(false);
                            handleLogout();
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-50 transition-colors text-left"
                        >
                          <Icon name="logout" size={18} className="text-red-600" />
                          <span className="text-sm font-medium text-red-600">Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-gray-900 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              <Icon name={mobileMenuOpen ? "close" : "menu"} size={24} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-4 space-y-3 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {/* Mobile Navigation Links */}
            {!currentSession && (
              <>
                <button 
                  onClick={() => { navigate('/recruiter'); setMobileMenuOpen(false); }}
                  className="w-full text-left px-4 py-3 text-gray-900 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                >
                  Recruitment
                </button>
                <button 
                  onClick={() => { navigate('/placement-training'); setMobileMenuOpen(false); }}
                  className="w-full text-left px-4 py-3 text-gray-900 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                >
                  Placement Training
                </button>
              </>
            )}
            
            {currentSession && (
              <>
                {!isCandidate && (
                  <button 
                    onClick={() => { navigate('/recruiter'); setMobileMenuOpen(false); }}
                    className="w-full text-left px-4 py-3 text-gray-900 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                  >
                    Recruitment
                  </button>
                )}
                <button 
                  onClick={() => { navigate('/placement-training'); setMobileMenuOpen(false); }}
                  className="w-full text-left px-4 py-3 text-gray-900 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                >
                  Placement Training
                </button>
              </>
            )}
            
            {/* Mobile Products Accordion */}
            <div className="border-t border-gray-100 pt-3">
              <button
                onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                className="w-full flex items-center justify-between px-4 py-3 text-gray-900 hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                <span>Products</span>
                <Icon name={mobileProductsOpen ? "expand_less" : "expand_more"} size={20} />
              </button>
              
              {mobileProductsOpen && (
                <div className="mt-2 ml-4 space-y-2">
                  <button
                    onClick={() => { navigate('/ai-trainer'); setMobileMenuOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="font-semibold">AI Interview Trainer</div>
                    <div className="text-xs text-gray-500">Practice with AI feedback</div>
                  </button>
                  <button
                    onClick={() => { navigate('/enhance-resume'); setMobileMenuOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="font-semibold">Enhance CV</div>
                    <div className="text-xs text-gray-500">AI-powered resume optimization</div>
                  </button>
                  <button
                    onClick={() => { navigate('/jobs'); setMobileMenuOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="font-semibold">Mock Interviews</div>
                    <div className="text-xs text-gray-500">Real-time interview practice</div>
                  </button>
                </div>
              )}
            </div>
            
            <button 
              onClick={() => { navigate('/jobs'); setMobileMenuOpen(false); }}
              className="w-full text-left px-4 py-3 text-gray-900 hover:bg-gray-50 rounded-lg font-medium transition-colors"
            >
              Jobs
            </button>
            
            <button 
              onClick={() => { navigate('/courses'); setMobileMenuOpen(false); }}
              className="w-full text-left px-4 py-3 text-gray-900 hover:bg-gray-50 rounded-lg font-medium transition-colors"
            >
              Courses
            </button>
            
            {/* Packages hidden for now */}
            
            {/* Mobile Company Accordion */}
            <div className="border-t border-gray-100 pt-3">
              <button
                onClick={() => setMobileCompanyOpen(!mobileCompanyOpen)}
                className="w-full flex items-center justify-between px-4 py-3 text-gray-900 hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                <span>Company</span>
                <Icon name={mobileCompanyOpen ? "expand_less" : "expand_more"} size={20} />
              </button>
              
              {mobileCompanyOpen && (
                <div className="mt-2 ml-4 space-y-2">
                  <button
                    onClick={() => { navigate('/about'); setMobileMenuOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    About Us
                  </button>
                  <button
                    onClick={() => { navigate('/support'); setMobileMenuOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    Help & Support
                  </button>
                  <button
                    onClick={() => { navigate('/collaboration'); setMobileMenuOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    Collaboration
                  </button>
                </div>
              )}
            </div>
            
            {/* Mobile Auth Buttons */}
            <div className="border-t border-gray-100 pt-4 space-y-3">
              {!currentSession ? (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    className="w-full"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }}
                    className="w-full bg-black text-white hover:bg-gray-800"
                  >
                    Explore
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    onClick={() => { navigate('/candidate/dashboard'); setMobileMenuOpen(false); }}
                    className="w-full"
                  >
                    Dashboard
                  </Button>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                        {currentSession.profile?.first_name?.charAt(0) || currentSession.email?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {currentSession.profile?.first_name || 'User'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {currentSession.email}
                        </p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <button
                        onClick={() => { navigate('/candidate/profile'); setMobileMenuOpen(false); }}
                        className="w-full flex items-center space-x-3 px-3 py-2 hover:bg-white rounded-lg transition-colors text-left"
                      >
                        <Icon name="person" size={18} className="text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">My Profile</span>
                      </button>
                      
                      {/* Packages hidden for now */}

                      <button
                        onClick={() => { navigate('/candidate/settings'); setMobileMenuOpen(false); }}
                        className="w-full flex items-center space-x-3 px-3 py-2 hover:bg-white rounded-lg transition-colors text-left"
                      >
                        <Icon name="settings" size={18} className="text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Settings</span>
                      </button>
                      
                      <button
                        onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                        className="w-full flex items-center space-x-3 px-3 py-2 hover:bg-red-50 rounded-lg transition-colors text-left"
                      >
                        <Icon name="logout" size={18} className="text-red-600" />
                        <span className="text-sm font-medium text-red-600">Logout</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};;

export default LandingHeader;
